import { useState, useRef, useCallback } from "react";

/**
 * Lightweight drag-to-reposition hook for a floating overlay element.
 * Purely presentational — does not touch any media/stream state.
 * Keeps the overlay's top-left corner within the viewport bounds.
 */
export default function useFloatingDrag(initialPosition, boxSize = { width: 220, height: 130 }) {
  const [position, setPosition] = useState(initialPosition);
  const dragState = useRef(null);

  const clamp = useCallback((x, y) => {
    const maxX = Math.max(0, window.innerWidth - boxSize.width);
    const maxY = Math.max(0, window.innerHeight - boxSize.height);
    return {
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    };
  }, [boxSize.width, boxSize.height]);

  const onDragHandleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
    };

    const onMove = (ev) => {
      if (!dragState.current) return;
      const dx = ev.clientX - dragState.current.startX;
      const dy = ev.clientY - dragState.current.startY;
      setPosition(clamp(dragState.current.originX + dx, dragState.current.originY + dy));
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [position, clamp]);

  return { position, onDragHandleMouseDown };
}
